/**
 * PLANIFICAÇÕES DO CUBO — que figuras de seis quadrados fecham um cubo.
 *
 * O teste é o do papel: rolar um cubo sobre a figura. Começa-se com o cubo apoiado num
 * quadrado; para passar a um quadrado vizinho, o cubo tomba sobre a aresta comum, e a
 * face que fica embaixo é a que cobre aquele quadrado. A figura é uma planificação se
 * e só se os seis quadrados recebem seis faces diferentes.
 *
 * A orientação do cubo é uma matriz de rotação inteira (do referencial do cubo para o
 * da mesa); tombar é multiplicá-la por uma de quatro rotações de 90°. Sem ponto
 * flutuante: as entradas são sempre −1, 0 ou 1.
 *
 * O módulo também enumera os 35 hexaminós livres — as 11 planificações e as 24 figuras
 * que não fecham —, e classifica as que não fecham pelo motivo, para o diagnóstico.
 */

export type Celula = readonly [number, number];

const VIZ: readonly Celula[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/** As 8 simetrias do quadrado: 4 rotações e 4 reflexões. */
const TRANSFORMACOES: readonly ((c: Celula) => Celula)[] = [
  ([x, y]) => [x, y],
  ([x, y]) => [-y, x],
  ([x, y]) => [-x, -y],
  ([x, y]) => [y, -x],
  ([x, y]) => [-x, y],
  ([x, y]) => [y, x],
  ([x, y]) => [x, -y],
  ([x, y]) => [-y, -x],
];

export const N_TRANSFORMACOES = TRANSFORMACOES.length;

/** Encosta a figura nos eixos (menor x e menor y iguais a 0) e ordena as células. */
export function normalizar(p: readonly Celula[]): Celula[] {
  const mx = Math.min(...p.map((c) => c[0]));
  const my = Math.min(...p.map((c) => c[1]));
  return p.map(([x, y]) => [x - mx, y - my] as Celula).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
}

const chaveDe = (p: readonly Celula[]): string =>
  normalizar(p)
    .map(([x, y]) => `${x},${y}`)
    .join(';');

export const transformar = (p: readonly Celula[], k: number): Celula[] =>
  normalizar(p.map(TRANSFORMACOES[k % TRANSFORMACOES.length] as (c: Celula) => Celula));

/** Identidade da figura a menos de rotação e reflexão. */
export function formaLivre(p: readonly Celula[]): string {
  return TRANSFORMACOES.map((_, k) => chaveDe(transformar(p, k))).sort()[0] as string;
}

export const largura = (p: readonly Celula[]): number => Math.max(...p.map((c) => c[0])) + 1;
export const altura = (p: readonly Celula[]): number => Math.max(...p.map((c) => c[1])) + 1;

let hexCache: readonly (readonly Celula[])[] | null = null;

/** Os 35 hexaminós livres, em ordem estável. */
export function hexaminos(): readonly (readonly Celula[])[] {
  if (hexCache) return hexCache;
  let atual = new Map<string, Celula[]>([['0,0', [[0, 0]]]]);
  for (let n = 2; n <= 6; n++) {
    const prox = new Map<string, Celula[]>();
    for (const p of atual.values()) {
      const tem = new Set(p.map(([x, y]) => `${x},${y}`));
      for (const [x, y] of p) {
        for (const [dx, dy] of VIZ) {
          if (tem.has(`${x + dx},${y + dy}`)) continue;
          const q = normalizar([...p, [x + dx, y + dy]]);
          prox.set(chaveDe(q), q);
        }
      }
    }
    atual = prox;
  }
  const livres = new Map<string, Celula[]>();
  for (const p of atual.values()) {
    const k = formaLivre(p);
    if (!livres.has(k)) livres.set(k, p);
  }
  hexCache = [...livres.entries()].sort(([a], [b]) => (a < b ? -1 : 1)).map(([, p]) => p);
  return hexCache;
}

// ---------------------------------------------------------------------------
// Rolar o cubo
// ---------------------------------------------------------------------------

type Mat = readonly number[];

const IDENTIDADE: Mat = [1, 0, 0, 0, 1, 0, 0, 0, 1];

function mul(A: Mat, B: Mat): Mat {
  const r: number[] = [];
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      let s = 0;
      for (let k = 0; k < 3; k++) s += (A[3 * i + k] as number) * (B[3 * k + j] as number);
      r.push(s);
    }
  }
  return r;
}

/**
 * Tombar o cubo para o quadrado vizinho. Para a direita (+x): a face da direita desce,
 * a de baixo vai para a esquerda, a de cima vai para a direita; o eixo y não muda.
 */
const TOMBAR: Readonly<Record<string, Mat>> = {
  '1,0': [0, 0, 1, 0, 1, 0, -1, 0, 0],
  '-1,0': [0, 0, -1, 0, 1, 0, 1, 0, 0],
  '0,1': [1, 0, 0, 0, 0, 1, 0, -1, 0],
  '0,-1': [1, 0, 0, 0, 0, -1, 0, 1, 0],
};

/** A face do cubo, no referencial dele, que está encostada na mesa: Rᵀ·(0, 0, −1). */
const faceDeBaixo = (R: Mat): string =>
  `${-(R[6] as number)},${-(R[7] as number)},${-(R[8] as number)}`;

/** Para cada quadrado da figura, a face do cubo que cai sobre ele. */
export function facesAoDobrar(p: readonly Celula[]): string[] {
  const indice = new Map(p.map(([x, y], i) => [`${x},${y}`, i]));
  const rot: (Mat | null)[] = p.map(() => null);
  rot[0] = IDENTIDADE;
  const fila = [0];
  while (fila.length > 0) {
    const i = fila.shift() as number;
    const [x, y] = p[i] as Celula;
    const R = rot[i] as Mat;
    for (const [dx, dy] of VIZ) {
      const j = indice.get(`${x + dx},${y + dy}`);
      if (j === undefined || rot[j]) continue;
      rot[j] = mul(TOMBAR[`${dx},${dy}`] as Mat, R);
      fila.push(j);
    }
  }
  return rot.map((R) => faceDeBaixo(R ?? IDENTIDADE));
}

export const fechaCubo = (p: readonly Celula[]): boolean =>
  p.length === 6 && new Set(facesAoDobrar(p)).size === 6;

// ---------------------------------------------------------------------------
// Classificação
// ---------------------------------------------------------------------------

export function temBloco2x2(p: readonly Celula[]): boolean {
  const s = new Set(p.map(([x, y]) => `${x},${y}`));
  return p.some(
    ([x, y]) => s.has(`${x + 1},${y}`) && s.has(`${x},${y + 1}`) && s.has(`${x + 1},${y + 1}`),
  );
}

/** Tamanhos das filas: sequências de quadrados vizinhos numa mesma linha ou coluna. */
function filas(p: readonly Celula[]): number[] {
  const s = new Set(p.map(([x, y]) => `${x},${y}`));
  const out: number[] = [];
  for (const [x, y] of p) {
    if (!s.has(`${x - 1},${y}`)) {
      let n = 1;
      while (s.has(`${x + n},${y}`)) n++;
      out.push(n);
    }
    if (!s.has(`${x},${y - 1}`)) {
      let n = 1;
      while (s.has(`${x},${y + n}`)) n++;
      out.push(n);
    }
  }
  return out;
}

export const maiorFila = (p: readonly Celula[]): number => Math.max(...filas(p));

/**
 * As famílias das 11 planificações, pelo número de quadrados em cada linha com a figura
 * deitada (4 × 3, ou 5 × 2 nas duas filas de três): a cruz e suas variações (1-4-1,
 * seis figuras), 2-3-1 (três), a escada 2-2-2 e as duas filas de três, 3-3.
 *
 * Contar pela maior fila em QUALQUER direção não serve: uma 2-3-1 pode ter também uma
 * coluna de três, e passaria por 3-3.
 */
export type Familia = '1-4-1' | '2-3-1' | '2-2-2' | '3-3';

export function familia(p: readonly Celula[]): Familia | null {
  if (!fechaCubo(p)) return null;
  const q = altura(p) > largura(p) ? transformar(p, 1) : normalizar(p);
  const porLinha = new Map<number, number>();
  for (const [, y] of q) porLinha.set(y, (porLinha.get(y) ?? 0) + 1);
  const perfil = [...porLinha.values()].sort((a, b) => a - b).join('-');
  if (perfil === '1-1-4') return '1-4-1';
  if (perfil === '1-2-3') return '2-3-1';
  if (perfil === '2-2-2') return '2-2-2';
  return '3-3';
}

/** Por que a figura não fecha: é o que dá nome ao erro de quem a aceitou. */
export type Motivo = 'bloco' | 'linha' | 'sobrepoe';

export function motivo(p: readonly Celula[]): Motivo | null {
  if (fechaCubo(p)) return null;
  if (temBloco2x2(p)) return 'bloco';
  if (maiorFila(p) >= 5) return 'linha';
  return 'sobrepoe';
}
